import { useTranslation } from 'react-i18next';
import type { FormStructure, FormQuestion } from '@affogato/shared';

interface FormPreviewProps {
  structure: FormStructure;
}

export function FormPreview({ structure }: FormPreviewProps) {
  const { t } = useTranslation('diagnostics');

  const renderQuestion = (question: FormQuestion) => {
    switch (question.type) {
      case 'scale':
        return (
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400">
              <span>{question.scaleConfig?.minLabel || '1'}</span>
              <span>{question.scaleConfig?.maxLabel || '5'}</span>
            </div>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((num) => (
                <button
                  key={num}
                  type="button"
                  className="flex-1 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg text-sm font-medium text-gray-700 dark:text-slate-300 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  {num}
                </button>
              ))}
            </div>
          </div>
        );

      case 'yes_no':
        return (
          <div className="flex gap-3">
            <button
              type="button"
              className="flex-1 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg text-sm font-medium text-gray-700 dark:text-slate-300 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              {t('public.yes') || 'Yes'}
            </button>
            <button
              type="button"
              className="flex-1 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg text-sm font-medium text-gray-700 dark:text-slate-300 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              {t('public.no') || 'No'}
            </button>
          </div>
        );

      case 'text':
        return (
          <textarea
            rows={3}
            disabled
            placeholder={t('builder.questionTypes.text')}
            className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg text-sm text-gray-700 dark:text-slate-300 resize-none"
          />
        );

      case 'single_choice':
        return (
          <div className="space-y-2">
            {question.choices?.map((choice) => (
              <label
                key={choice.id}
                className="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg cursor-pointer hover:border-primary-500 transition-colors"
              >
                <input
                  type="radio"
                  name={`preview-${question.id}`}
                  disabled
                  className="w-4 h-4 text-primary-600 border-gray-300 dark:border-slate-600"
                />
                <span className="text-sm text-gray-700 dark:text-slate-300">{choice.text}</span>
              </label>
            ))}
          </div>
        );

      case 'multiple_choice':
        return (
          <div className="space-y-2">
            {question.choices?.map((choice) => (
              <label
                key={choice.id}
                className="flex items-center gap-3 p-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg cursor-pointer hover:border-primary-500 transition-colors"
              >
                <input
                  type="checkbox"
                  disabled
                  className="w-4 h-4 rounded text-primary-600 border-gray-300 dark:border-slate-600"
                />
                <span className="text-sm text-gray-700 dark:text-slate-300">{choice.text}</span>
              </label>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {structure.sections.map((section, sectionIndex) => (
        <div
          key={section.id}
          className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden"
        >
          {/* Section Header */}
          {structure.settings.showSectionTitles && (
            <div className="px-6 py-4 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 text-xs font-medium rounded">
                  {t('public.section')} {sectionIndex + 1}
                </span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mt-2">
                {section.title}
              </h3>
              {section.description && (
                <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                  {section.description}
                </p>
              )}
            </div>
          )}

          {/* Questions */}
          <div className="p-6 space-y-6">
            {section.questions.map((question, qIndex) => (
              <div key={question.id} className="space-y-3">
                <div className="flex items-start gap-2">
                  <span className="text-sm font-medium text-gray-400 dark:text-slate-500">
                    {qIndex + 1}.
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {question.text}
                      {question.required && (
                        <span className="text-red-500 ml-1">*</span>
                      )}
                    </p>
                    {question.description && (
                      <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                        {question.description}
                      </p>
                    )}
                  </div>
                </div>
                {renderQuestion(question)}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Progress Bar Preview */}
      {structure.settings.showProgressBar && (
        <div className="fixed bottom-0 left-0 right-0 h-1 bg-gray-200 dark:bg-slate-700">
          <div className="h-full w-1/3 bg-primary-500 transition-all" />
        </div>
      )}
    </div>
  );
}
